/**
 * Opportunity Email Draft Tab Component
 * Auto-generates email drafts and allows iteration/editing
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, LoadingSpinner } from '@tracekit/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import {
  Mail,
  FileText,
  Send,
  Eye,
  X,
  Edit,
  RefreshCw,
  Save,
  Plus,
  AlertCircle,
  Download,
} from 'lucide-react';

interface OpportunityEmailDraftTabProps {
  opportunityId: string;
  projectId: string;
  opportunity: any; // Opportunity entity data
}

interface EmailDraft {
  subject: string;
  body: string;
  recipients: Array<{ name: string; email: string; id?: string; type?: string }>;
  attachments: Array<{ artifact_id: string; filename: string; name: string }>;
  summary: string;
  key_points: string[];
}

export function OpportunityEmailDraftTab({
  opportunityId,
  projectId,
  opportunity,
}: OpportunityEmailDraftTabProps) {
  const [additionalEmails, setAdditionalEmails] = useState<string[]>(['']);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editSuggestions, setEditSuggestions] = useState('');
  const api = getAPIClient();
  const queryClient = useQueryClient();

  // Auto-generate email draft on mount - only run once, don't retry automatically
  const { data: emailDraft, isLoading: generatingDraft, isError: draftError, error: draftErrorDetails, refetch: regenerateDraft } = useQuery<EmailDraft>({
    queryKey: ['email-draft', opportunityId, projectId], // Removed additionalEmails from key to prevent unnecessary refetches
    queryFn: async () => {
      // API client's post() method returns response.data directly (not the full axios response)
      // So 'data' here is already the unwrapped response data
      const data = await api.post<EmailDraft>(
        `/api/opportunities/${opportunityId}/email-draft`,
        {
          recipient_ids: [], // No internal staff - only client contacts from opportunity
          additional_recipients: additionalEmails.filter((e) => e && e.includes('@')),
          subject: undefined, // Auto-generate
          include_documents: true,
          summary_length: 300,
        },
        {
          params: { project_id: projectId },
        }
      );
      
      console.log('[EmailDraft] API response data:', data);
      console.log('[EmailDraft] Response data type:', typeof data);
      
      // Validate response has required fields
      if (!data) {
        console.error('[EmailDraft] API returned null/undefined response');
        throw new Error('Empty response from email draft API');
      }
      if (!data.subject || !data.body) {
        console.error('[EmailDraft] API returned incomplete response:', { hasSubject: !!data.subject, hasBody: !!data.body, data });
        throw new Error('Invalid response from email draft API - missing required fields (subject or body)');
      }
      return data;
    },
    enabled: !!opportunityId && !!projectId,
    retry: false, // Don't retry automatically - user can click retry button
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    refetchOnMount: false, // Don't refetch on mount if data exists (prevent duplicate calls)
    refetchOnReconnect: false, // Don't refetch on reconnect
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Initialize edited values when draft is generated
  useEffect(() => {
    if (emailDraft && !isEditing) {
      setEditedSubject(emailDraft.subject);
      setEditedBody(emailDraft.body);
    }
  }, [emailDraft, isEditing]);

  const addAdditionalEmail = () => {
    setAdditionalEmails([...additionalEmails, '']);
  };

  const updateAdditionalEmail = (index: number, value: string) => {
    const newEmails = [...additionalEmails];
    newEmails[index] = value;
    setAdditionalEmails(newEmails);
    // Don't auto-regenerate on every keystroke - user can click regenerate button
  };

  const removeAdditionalEmail = (index: number) => {
    const newEmails = additionalEmails.filter((_, i) => i !== index);
    setAdditionalEmails(newEmails.length > 0 ? newEmails : ['']);
    // Don't auto-regenerate - user can click regenerate button if needed
  };

  const handleRegenerateDraft = () => {
    setIsEditing(false);
    setEditSuggestions('');
    // Invalidate and refetch with current additional emails
    queryClient.invalidateQueries({ queryKey: ['email-draft', opportunityId, projectId] });
    regenerateDraft();
  };

  const handleApplySuggestions = async () => {
    if (!editSuggestions.trim() || !emailDraft) return;
    
    // Call API to regenerate with suggestions
    try {
      const response = await api.post(
        `/api/opportunities/${opportunityId}/email-draft/regenerate`,
        {
          base_draft: emailDraft,
          suggestions: editSuggestions,
          edited_subject: editedSubject !== emailDraft.subject ? editedSubject : undefined,
          edited_body: editedBody !== emailDraft.body ? editedBody : undefined,
        },
        {
          params: { project_id: projectId },
        }
      );
      
      // Update draft with regenerated version
      if (response.data) {
        setEditedSubject(response.data.subject);
        setEditedBody(response.data.body);
        setEditSuggestions('');
        setIsEditing(false);
      }
    } catch (error: any) {
      console.error('[EmailDraft] Regeneration failed:', error);
      // Fallback: just update local state if API not available yet
      if (error.response?.status === 404) {
        // API endpoint not implemented yet - just update local state
        setEditSuggestions('');
        setIsEditing(false);
      } else {
        alert(`Failed to apply suggestions: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // Convert markdown to HTML for email body
  const convertMarkdownToHTML = (text: string): string => {
    if (!text) return '';
    
    let html = text;
    
    // Escape existing HTML first (but preserve already-formatted HTML)
    // Only escape if it doesn't look like HTML already
    if (!html.includes('<') || html.match(/<[^>]+>/g)?.length === 0) {
      html = html
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
    
    // Headers (must be on new lines)
    html = html.replace(/^### (.+)$/gm, '<h3 style="font-size: 18px; font-weight: 600; margin-top: 16px; margin-bottom: 8px; color: #111827;">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 style="font-size: 20px; font-weight: 700; margin-top: 20px; margin-bottom: 12px; color: #111827;">$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1 style="font-size: 24px; font-weight: 700; margin-top: 24px; margin-bottom: 16px; color: #111827;">$1</h1>');
    
    // Bold (before italic to handle **)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong style="font-weight: 600;">$1</strong>');
    
    // Italic
    html = html.replace(/\*([^*]+)\*/g, '<em style="font-style: italic;">$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em style="font-style: italic;">$1</em>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #2563eb; text-decoration: underline;">$1</a>');
    
    // Code blocks
    html = html.replace(/```([^`]+)```/g, '<pre style="background-color: #f3f4f6; padding: 12px; border-radius: 4px; overflow-x: auto; margin: 12px 0;"><code style="font-family: monospace; font-size: 14px;">$1</code></pre>');
    
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code style="background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 14px;">$1</code>');
    
    // Process lists and paragraphs line by line
    const lines = html.split('\n');
    const processedLines: string[] = [];
    let inUl = false;
    let inOl = false;
    let currentParagraph: string[] = [];
    
    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        processedLines.push(`<p style="margin: 12px 0; line-height: 1.6;">${currentParagraph.join(' ')}</p>`);
        currentParagraph = [];
      }
    };
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      if (!trimmed) {
        flushParagraph();
        if (inUl) {
          processedLines.push('</ul>');
          inUl = false;
        }
        if (inOl) {
          processedLines.push('</ol>');
          inOl = false;
        }
        continue;
      }
      
      // Check if it's already a tag (header, code block, etc.)
      if (trimmed.startsWith('<h') || trimmed.startsWith('<pre') || trimmed.startsWith('<code')) {
        flushParagraph();
        if (inUl) {
          processedLines.push('</ul>');
          inUl = false;
        }
        if (inOl) {
          processedLines.push('</ol>');
          inOl = false;
        }
        processedLines.push(trimmed);
        continue;
      }
      
      // Check for bullet list
      const bulletMatch = trimmed.match(/^[-*•]\s+(.+)$/);
      if (bulletMatch) {
        flushParagraph();
        if (inOl) {
          processedLines.push('</ol>');
          inOl = false;
        }
        if (!inUl) {
          processedLines.push('<ul style="margin: 12px 0; padding-left: 24px; list-style-type: disc;">');
          inUl = true;
        }
        processedLines.push(`<li style="margin-bottom: 4px;">${bulletMatch[1]}</li>`);
        continue;
      }
      
      // Check for numbered list
      const numberedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
      if (numberedMatch) {
        flushParagraph();
        if (inUl) {
          processedLines.push('</ul>');
          inUl = false;
        }
        if (!inOl) {
          processedLines.push('<ol style="margin: 12px 0; padding-left: 24px; list-style-type: decimal;">');
          inOl = true;
        }
        processedLines.push(`<li style="margin-bottom: 4px;">${numberedMatch[1]}</li>`);
        continue;
      }
      
      // Regular text - add to paragraph
      flushParagraph();
      if (inUl) {
        processedLines.push('</ul>');
        inUl = false;
      }
      if (inOl) {
        processedLines.push('</ol>');
        inOl = false;
      }
      currentParagraph.push(trimmed);
    }
    
    flushParagraph();
    if (inUl) {
      processedLines.push('</ul>');
    }
    if (inOl) {
      processedLines.push('</ol>');
    }
    
    html = processedLines.join('\n');
    
    return html;
  };

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      if (!emailDraft) {
        throw new Error('No email draft available');
      }
      
      // Collect recipient emails (internal team members from additionalEmails)
      const toRecipients = additionalEmails.filter((e) => e && e.trim() && e.includes('@'));
      if (toRecipients.length === 0) {
        throw new Error('Please add at least one recipient email address');
      }
      
      // Collect attachment artifact IDs
      const attachmentIds = emailDraft.attachments?.map((att) => att.artifact_id).filter(Boolean) || [];
      
      // Convert markdown to HTML if needed (check if body contains markdown patterns)
      let emailBody = editedBody || emailDraft.body;
      // If body contains markdown patterns but not HTML tags, convert it
      if (emailBody && (emailBody.includes('**') || emailBody.includes('__') || emailBody.includes('`') || emailBody.match(/^#+\s/m))) {
        // Check if it's already HTML (has HTML tags)
        if (!emailBody.includes('<') || emailBody.match(/<[^>]+>/g)?.length === 0) {
          emailBody = convertMarkdownToHTML(emailBody);
        } else {
          // Mixed content - convert markdown patterns within HTML
          emailBody = emailBody
            .replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
            .replace(/__([^_]+)__/g, '<strong style="font-weight: 600;">$1</strong>')
            .replace(/\*([^*]+)\*/g, '<em style="font-style: italic;">$1</em>')
            .replace(/_([^_]+)_/g, '<em style="font-style: italic;">$1</em>');
        }
      }
      
      const response = await api.post(
        `/api/opportunities/${opportunityId}/email-draft/send`,
        {
          to_recipients: toRecipients,
          cc_recipients: [], // Can be extended later
          subject: editedSubject || emailDraft.subject,
          body: emailBody,
          attachment_artifact_ids: attachmentIds,
        },
        {
          params: { project_id: projectId },
        }
      );
      
      return response;
    },
    onSuccess: (response) => {
      console.log('[EmailDraft] Email sent successfully:', response);
      alert(`Email sent successfully! ${response.message || ''}`);
    },
    onError: (error: any) => {
      console.error('[EmailDraft] Failed to send email:', error);
      alert(`Failed to send email: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleSendEmail = () => {
    if (!emailDraft) {
      alert('No email draft available. Please wait for the draft to generate.');
      return;
    }
    
    const toRecipients = additionalEmails.filter((e) => e && e.includes('@'));
    if (toRecipients.length === 0) {
      alert('Please add at least one recipient email address before sending.');
      return;
    }
    
    if (!confirm(`Send email to ${toRecipients.length} recipient(s)?`)) {
      return;
    }
    
    sendEmailMutation.mutate();
  };

  // Internal team members (primary TO recipients) - those with an 'id' field (staff entity IDs)
  const internalRecipients = emailDraft?.recipients?.filter(r => r.id && r.type !== 'client_contact' && r.type !== 'client_contact_placeholder') || [];

  return (
    <div className="space-y-6">
      {/* Client Recipients (Auto-extracted from Opportunity) */}
      {generatingDraft ? (
        <Card className="p-6">
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
            <span className="ml-3 text-gray-600 dark:text-theme-text-secondary">
              Generating email draft...
            </span>
          </div>
        </Card>
      ) : draftError ? (
        <Card className="p-6">
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-400 dark:text-red-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-theme-text-secondary mb-2">
              Failed to generate email draft.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {draftErrorDetails?.message || draftErrorDetails?.response?.data?.detail || 'An error occurred while generating the email draft.'}
            </p>
            <Button variant="primary" onClick={() => regenerateDraft()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </Card>
      ) : emailDraft && emailDraft.subject && emailDraft.body ? (
        <>
          {/* Recipients Section - Scalable UI */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-4">
              Email Recipients
            </h3>

            {/* Primary Recipients (Internal Team - TO) */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-theme-text-secondary mb-2">
                Primary Recipients (TO) - Internal Team Members
              </label>
              {internalRecipients.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {internalRecipients.map((recipient, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-theme-text">
                          {recipient.name || recipient.email}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
                          {recipient.email}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 italic">
                  No internal team members selected. Add recipients below.
                </p>
              )}
              
              {/* Add Internal Team Members */}
              <div className="space-y-2">
                {additionalEmails.map((email, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => updateAdditionalEmail(index, e.target.value)}
                      placeholder="team-member@company.com"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm"
                    />
                    {additionalEmails.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAdditionalEmail(index)}
                        className="flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={addAdditionalEmail} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Team Member
                </Button>
              </div>
            </div>
          </Card>

          {/* Email Draft - Auto-generated with Edit Capability */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
                Email Draft
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerateDraft}
                  disabled={generatingDraft}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
                <div className="relative">
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={handleSendEmail}
                    disabled={sendEmailMutation.isPending || !emailDraft || additionalEmails.filter((e) => e && e.trim() && e.includes('@')).length === 0}
                    title={additionalEmails.filter((e) => e && e.trim() && e.includes('@')).length === 0 ? 'Please add at least one recipient email address' : 'Send email'}
                  >
                    {sendEmailMutation.isPending ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                  {additionalEmails.filter((e) => e && e.trim() && e.includes('@')).length === 0 && emailDraft && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Add at least one recipient email above to enable sending
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Subject - Editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-theme-text-secondary mb-2">
                  Subject
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                  />
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                    <p className="text-gray-900 dark:text-theme-text font-medium">
                      {editedSubject || emailDraft.subject}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Email Body - Editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-theme-text-secondary mb-2">
                  Email Body
                </label>
                {isEditing ? (
                  <textarea
                    value={editedBody.replace(/<[^>]*>/g, '')} // Strip HTML for editing
                    onChange={(e) => setEditedBody(e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 font-mono text-sm"
                  />
                ) : showPreview ? (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-800">
                    <div
                      dangerouslySetInnerHTML={{ __html: editedBody || emailDraft.body }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                    <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
                      Preview hidden - click "Show Preview" to view
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Edit Suggestions */}
              {isEditing && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <label className="block text-sm font-medium text-gray-700 dark:text-theme-text-secondary mb-2">
                    Suggestions for Improvement (optional)
                  </label>
                  <textarea
                    value={editSuggestions}
                    onChange={(e) => setEditSuggestions(e.target.value)}
                    placeholder="E.g., 'Make the tone more formal', 'Add more details about the deadline', 'Emphasize our past experience'"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                  />
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleApplySuggestions}
                      disabled={!editSuggestions.trim()}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Apply Suggestions
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setEditSuggestions('');
                        setEditedSubject(emailDraft.subject);
                        setEditedBody(emailDraft.body);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Attachments - Scalable UI */}
              {emailDraft.attachments && emailDraft.attachments.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-theme-text-secondary mb-2">
                    Attachments ({emailDraft.attachments.length})
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {emailDraft.attachments.map((att, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <span className="text-sm text-gray-900 dark:text-theme-text truncate flex-1" title={att.name || att.filename}>
                          {att.name || att.filename}
                        </span>
                        {att.artifact_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              window.open(`/api/artifacts/${att.artifact_id}/download`, '_blank');
                            }}
                            className="flex-shrink-0"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </>
      ) : (
        <Card className="p-6">
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-theme-text-secondary">
              Failed to generate email draft. Please try again.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
